import { Link } from "react-router-dom";

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}>

      {/* NAV */}
      <header className="sticky top-0 z-50 bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 focus:outline-none">
            <img src={`${process.env.PUBLIC_URL}/SAL-logoR3.jpg`} alt="SAL Mine Logo" className="h-10 w-10 object-contain rounded" />
            <div className="flex flex-col items-start">
              <span className="text-xl font-extrabold text-[#2d348f] tracking-tight leading-tight">SAL Mine</span>
              <span className="text-[10px] text-[#837f90] font-medium leading-tight">
                Patent No. US 10,940,711 B2 &nbsp;·&nbsp; Patented in US, Europe &amp; China
              </span>
            </div>
          </Link>
          <div className="flex items-center gap-6">
            <Link to="/" className="text-sm font-medium text-gray-700 hover:text-[#2d348f] transition-colors hidden md:block">
              Home
            </Link>
            <Link
              to="/login"
              className="px-5 py-2 rounded-lg bg-gradient-to-r from-[#445eed] to-[#2d348f] text-white font-bold text-sm hover:from-[#3916ab] hover:to-[#4962d8] transition-all shadow-md"
            >
              Login
            </Link>
          </div>
        </div>
      </header>

      {/* HERO BANNER */}
      <div className="w-full bg-[#445eed] py-5 px-6 text-center">
        <p className="text-white font-semibold text-base tracking-wide">
          Your information.&nbsp;&nbsp;·&nbsp;&nbsp;Your life.&nbsp;&nbsp;·&nbsp;&nbsp;Protected always.
        </p>
      </div>

      {/* HERO SECTION */}
      <section className="relative w-full min-h-[420px] flex items-center overflow-hidden">
        <img
          src={`${process.env.PUBLIC_URL}/opt-stretcher.jpg`}
          alt="Emergency Medical"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1a1f5e]/90 via-[#2d348f]/75 to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-20 w-full">
          <p className="text-blue-200 text-sm font-semibold uppercase tracking-widest mb-3">Our Story</p>
          <h1 className="text-4xl md:text-5xl font-extrabold text-white leading-tight mb-4 max-w-2xl drop-shadow-lg">
            About Save A Life Mine
          </h1>
          <p className="text-lg text-blue-100 max-w-xl leading-relaxed">
            In the moments that matter most, every second counts. We built S.A.L Mine to close the gap between a patient and the care they deserve.
          </p>
        </div>
      </section>

      {/* MISSION SECTION */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-14 items-center">
            <div>
              <p className="text-xs font-bold text-[#445eed] uppercase tracking-widest mb-3">Our Mission</p>
              <h2 className="text-3xl font-extrabold text-[#2d348f] mb-6 leading-snug">
                Bridging the gap in emergency care — one card at a time.
              </h2>
              <p className="text-gray-600 leading-relaxed mb-5">
                At Save A Life Mine, we believe that in the moments that matter most, every second counts.
              </p>
              <p className="text-gray-600 leading-relaxed mb-5">
                We created Save A Life Mine after recognizing a gap that exists in emergency care — first responders arriving on scene with a patient who cannot speak for themselves, with no way to quickly know their allergies, medications, conditions, or emergency contacts. That gap costs lives.
              </p>
              <p className="text-gray-600 leading-relaxed">
                During one's lifetime there is an emergency either for yourself or for someone else in your life. TIME — down to seconds — means the difference between living and dying. Lack of information at the time of an emergency could mean the difference between life and death.
              </p>
            </div>
            <div className="flex justify-center">
              <div className="relative">
                <div className="absolute -top-4 -left-4 w-full h-full bg-[#445eed]/10 rounded-2xl" />
                <img
                  src={`${process.env.PUBLIC_URL}/SAL Card 4-25-B.png`}
                  alt="SAL Mine Medical ID Card"
                  className="relative rounded-2xl shadow-2xl w-full max-w-sm border-4 border-white"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STORY SECTION */}
      <section className="py-20 bg-[#f7f8fa]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-xs font-bold text-[#445eed] uppercase tracking-widest mb-3">Our Solution</p>
          <h2 className="text-3xl font-extrabold text-[#2d348f] mb-8 leading-snug">
            Simple. Powerful. Personal.
          </h2>
          <p className="text-gray-600 leading-relaxed mb-6 text-lg">
            A medical ID card built for the modern world — waterproof, durable, and digitally connected. When a first responder reads and scans your S.A.L Mine card, they instantly have access to everything they need to treat you safely and effectively.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mt-10">
            {[
              { icon: "🚫", label: "No guesswork." },
              { icon: "⏱️", label: "No delays." },
              { icon: "❤️", label: "Just care." },
            ].map((item) => (
              <div key={item.label} className="bg-white rounded-2xl border border-[#e5e7eb] shadow-md px-8 py-6 flex flex-col items-center gap-2 flex-1">
                <span className="text-3xl">{item.icon}</span>
                <span className="font-extrabold text-[#2d348f] text-lg">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMMITMENT SECTION */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-14 items-center">
            <div className="flex justify-center order-2 md:order-1">
              <div className="bg-gradient-to-br from-[#f7f8fa] to-[#e6e9f5] rounded-2xl border border-[#e5e7eb] shadow-lg p-8 max-w-sm w-full flex flex-col gap-5">
                {[
                  { emoji: "🔒", title: "HIPAA-Compliant", desc: "Patient data protected by enterprise-grade encryption and full HIPAA compliance." },
                  { emoji: "🌍", title: "Globally Patented", desc: "Patent No. US 10,940,711 B2 — protected in the United States, Europe, and China." },
                  { emoji: "🏥", title: "Built with Dignity", desc: "Designed to ensure every patient receives the care they deserve, even when they cannot speak." },
                ].map((item) => (
                  <div key={item.title} className="flex items-start gap-4">
                    <span className="text-2xl mt-0.5">{item.emoji}</span>
                    <div>
                      <div className="font-bold text-[#2d348f] text-sm">{item.title}</div>
                      <div className="text-xs text-[#837f90] leading-relaxed mt-0.5">{item.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="order-1 md:order-2">
              <p className="text-xs font-bold text-[#445eed] uppercase tracking-widest mb-3">Our Commitment</p>
              <h2 className="text-3xl font-extrabold text-[#2d348f] mb-6 leading-snug">
                Founded on security and patient dignity.
              </h2>
              <p className="text-gray-600 leading-relaxed mb-5">
                Founded with a commitment to HIPAA-compliant security and patient dignity, Save A Life Mine bridges the space between who you are and the care you deserve — even when you can't advocate for yourself.
              </p>
              <p className="text-2xl font-extrabold text-[#2d348f] italic">
                "Your information. Your life. Protected always."
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* WHY SAL SECTION */}
      <section className="py-20 bg-gradient-to-br from-[#f7f8fa] to-[#e6e9f5]">
        <div className="max-w-4xl mx-auto px-6">
          <p className="text-xs font-bold text-[#445eed] uppercase tracking-widest mb-3 text-center">The Stakes</p>
          <h2 className="text-3xl font-extrabold text-[#2d348f] text-center mb-6 leading-snug">
            Why is S.A.L Mine so important to have?
          </h2>
          <div className="bg-white rounded-2xl border border-[#e5e7eb] shadow-lg p-8 md:p-10">
            <p className="text-gray-600 leading-relaxed mb-5">
              If you had a child and they got hurt in school or while playing sports, wouldn't you want to be informed immediately and have their medical information readily available?
            </p>
            <p className="text-gray-600 leading-relaxed mb-5">
              Think what could happen if you needed a blood transfusion, but the attendant did not know your blood type and had to do a blood test first. With S.A.L Mine, the hospital will be informed to have your blood type ready for transfusion when you arrive.
            </p>
            <p className="text-gray-600 leading-relaxed mb-8">
              Without S.A.L Mine, you could be given a medication you are allergic to — like iodine. We are here to make sure that does not happen in your time of dire need.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { emoji: "🩸", stat: "Blood Type", desc: "Ready on arrival — no delays for testing." },
                { emoji: "⚠️", stat: "Allergy Alerts", desc: "No dangerous medication errors." },
                { emoji: "👶", stat: "Every Age", desc: "Children, adults, and seniors all protected." },
              ].map((item) => (
                <div key={item.stat} className="bg-[#f7f8fa] rounded-xl border border-[#e5e7eb] p-5 text-center">
                  <div className="text-3xl mb-2">{item.emoji}</div>
                  <div className="font-extrabold text-[#2d348f] text-sm mb-1">{item.stat}</div>
                  <div className="text-xs text-[#837f90] leading-relaxed">{item.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-16 bg-gradient-to-r from-[#2d348f] to-[#445eed]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-2xl font-extrabold text-white mb-3">Ready to protect yourself and your loved ones?</h2>
          <p className="text-blue-200 mb-8">Join thousands of patients and caregivers who trust S.A.L Mine every day.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/signup"
              className="px-8 py-3 bg-white text-[#2d348f] font-bold rounded-lg shadow-lg hover:bg-blue-50 transition-all"
            >
              Get Started
            </Link>
            <Link
              to="/login"
              className="px-8 py-3 border-2 border-white text-white font-bold rounded-lg hover:bg-white/10 transition-all"
            >
              Login
            </Link>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#1a1f5e] text-white py-8">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-blue-300">
          <div className="flex items-center gap-2">
            <img src={`${process.env.PUBLIC_URL}/SAL-logoR3.jpg`} alt="SAL Mine" className="h-6 w-6 object-contain rounded" />
            <span className="font-bold text-white">SAL Mine</span>
          </div>
          <span>© {new Date().getFullYear()} Save A Life Mine. All rights reserved.</span>
          <div className="flex gap-4">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <Link to="/terms-and-conditions" className="hover:text-white transition-colors">Terms of Use</Link>
          </div>
        </div>
      </footer>

    </div>
  );
}
